import { istDateKey, istISOString, formatISTDateTime, formatCalendarDate } from "../../lib/admin-time";
import { metricTooltip, timeSeriesInteraction } from "../../lib/growth-chart";
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useState, useEffect, useContext, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import { APIContext } from "../../api/api";
import HelmetLayout from "../../components/HelmetLayout";
import { PulseLoader } from "react-spinners";
import { toast } from "react-toastify";
import {
  Zap,
  Globe,
  Sliders,
  FileText,
  Users,
  RefreshCw,
  Download,
  Search,
  ExternalLink,
  Eye,
  Copy,
  Check,
  ChevronLeft,
  ChevronRight,
  Sparkles,
  BarChart3,
  PieChart,
  X,
  User,
  ArrowUpRight,
  UserCheck,
  UserX,
  Activity,
} from "lucide-react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar, Line, Doughnut } from "react-chartjs-2";

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

// Format numbers with K/M suffix with 2 decimals precision
export const formatMetricNumber = (num) => {
  if (num === null || num === undefined || isNaN(num)) return "0";
  const abs = Math.abs(Number(num));
  if (abs >= 1000000) {
    return `${(Number(num) / 1000000).toFixed(2)}M`;
  }
  if (abs >= 1000) {
    return `${(Number(num) / 1000).toFixed(2)}K`;
  }
  return Number(num).toLocaleString();
};

const StatCard = ({ title, value, badge, icon: Icon, color }) => (
  <div className="bg-white rounded-xl p-5 border border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-300 flex flex-col justify-between">
    <div className="flex justify-between items-start">
      <div>
        <p className="text-slate-500 text-xs font-semibold uppercase tracking-wider mb-1.5">
          {title}
        </p>
        <h3
          className="text-2xl font-bold text-slate-900 tracking-tight"
          title={typeof value === "number" ? `${value.toLocaleString()} credits` : undefined}
        >
          {formatMetricNumber(value)}
        </h3>
      </div>
      <div className={`p-2.5 rounded-lg ${color.bg}`}>
        <Icon size={20} className={color.text} />
      </div>
    </div>
    {badge && (
      <div className="mt-4 pt-3 border-t border-slate-100 flex items-center">
        <span
          className={`px-2 py-0.5 rounded-full font-semibold text-[11px] whitespace-nowrap ${color.badge}`}
        >
          {badge}
        </span>
      </div>
    )}
  </div>
);

export default function AIUsage() {
  const { getAiUsageAnalytics } = useContext(APIContext);
  const [data, setData] = useState(null);
  const requestSequence = useRef(0);
  const [loadError, setLoadError] = useState(false);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // Tabs: 'users' | 'logs'
  const [activeTab, setActiveTab] = useState("users");

  // Timeframe filter for charts: 7, 15, 30, 90, 'all'
  const [timeframe, setTimeframe] = useState(30);
  const [chartType, setChartType] = useState("bar"); // 'bar' | 'line'

  // Top Users Table filters & pagination
  const [userSearchQuery, setUserSearchQuery] = useState("");
  const [userTypeFilter, setUserTypeFilter] = useState("all"); // 'all' | 'registered' | 'guest'
  const [userPage, setUserPage] = useState(1);
  const [userPageSize, setUserPageSize] = useState(25);

  // Activity Logs Table filters & pagination
  const [searchQuery, setSearchQuery] = useState("");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Prompt inspect modal
  const [inspectedPrompt, setInspectedPrompt] = useState(null);
  const [copiedPromptId, setCopiedPromptId] = useState(null);

  const fetchData = async (isRefresh = false) => {
    const sequence = ++requestSequence.current;
    setLoadError(false);
    if (!isRefresh) setData(null);
    if (isRefresh) setRefreshing(true);
    else setLoading(true);

    try {
      if (typeof getAiUsageAnalytics === "function") {
        const res = await getAiUsageAnalytics(timeframe);
        if (sequence === requestSequence.current) setData(res);
      } else {
        console.warn("getAiUsageAnalytics is not available in APIContext");
      }
    } catch (err) {
      if (sequence !== requestSequence.current) return;
      setLoadError(true);
      setData(null);
      console.error("Failed to load AI usage analytics:", err);
      toast.error("Failed to load AI usage analytics");
    } finally {
      if (sequence === requestSequence.current) { setLoading(false); setRefreshing(false); }
    }
  };

  useEffect(() => {
    setUserPage(1);
    setCurrentPage(1);
    fetchData();
    return () => { requestSequence.current++; };
  }, [timeframe]);

  const summary = {
    total_credits: 0,
    fresh_website_credits: 0,
    edit_credits: 0,
    other_tool_credits: 0,
    total_active_users: 0,
    total_operations: 0,
    fresh_websites_count: 0,
    edits_count: 0,
    claimed_guest_count: 0,
    unclaimed_guest_count: 0,
    unclaimed_guest_credits: 0,
    ...data?.summary,
  };

  const filteredTimeline = data?.timeline || [];
  const rangeLabel = timeframe === "all" ? "All time" : `Last ${timeframe} days`;

  // Chart 1: Credit Consumption Over Time
  const consumptionChartData = useMemo(() => {
    const labels = filteredTimeline.map((item) => formatCalendarDate(item.date));

    if (chartType === "bar") {
      return {
        labels,
        datasets: [
          {
            label: "Fresh Websites (15k)",
            data: filteredTimeline.map((item) => item.fresh_website_credits || 0),
            backgroundColor: "#623ce7",
            borderRadius: 4,
            stack: "usage",
          },
          {
            label: "Website Edits (1k)",
            data: filteredTimeline.map((item) => item.edit_credits || 0),
            backgroundColor: "#10b981",
            borderRadius: 4,
            stack: "usage",
          },
          {
            label: "Other AI Tools",
            data: filteredTimeline.map((item) => item.other_tool_credits || 0),
            backgroundColor: "#3b82f6",
            borderRadius: 4,
            stack: "usage",
          },
        ],
      };
    }

    // Line Chart
    return {
      labels,
      datasets: [
        {
          label: "Total Daily Credits",
          data: filteredTimeline.map((item) => item.total_credits || 0),
          borderColor: "#623ce7",
          backgroundColor: "rgba(98, 60, 231, 0.08)",
          fill: true,
          tension: 0.35,
          pointRadius: 3,
          pointHoverRadius: 6,
          borderWidth: 2,
        },
        {
          label: "Fresh Websites",
          data: filteredTimeline.map((item) => item.fresh_website_credits || 0),
          borderColor: "#8b5cf6",
          backgroundColor: "transparent",
          tension: 0.3,
          pointRadius: 2,
          borderWidth: 1.5,
          borderDash: [4, 4],
        },
        {
          label: "Website Edits",
          data: filteredTimeline.map((item) => item.edit_credits || 0),
          borderColor: "#10b981",
          backgroundColor: "transparent",
          tension: 0.3,
          pointRadius: 2,
          borderWidth: 1.5,
          borderDash: [4, 4],
        },
        {
          label: "Other AI Tools",
          data: filteredTimeline.map((item) => item.other_tool_credits || 0),
          borderColor: "#3b82f6",
          backgroundColor: "transparent",
          tension: 0.3,
          pointRadius: 2,
          borderWidth: 1.5,
          borderDash: [4, 4],
        },
      ],
    };
  }, [filteredTimeline, chartType]);

  const consumptionChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "top",
        labels: {
          boxWidth: 12,
          usePointStyle: true,
          pointStyle: "circle",
          font: { family: "sans-serif", size: 12 },
          color: "#475569",
        },
      },
      tooltip: metricTooltip({
        unit: "credits",
        footer: chartType === "bar"
          ? (items) => `Total Daily Credits: ${Number(filteredTimeline[items[0]?.dataIndex]?.total_credits || 0).toLocaleString()} credits`
          : undefined,
      }),
    },
    interaction: timeSeriesInteraction,
    scales: {
      x: {
        grid: { display: false },
        ticks: { font: { size: 11 }, color: "#64748b" },
      },
      y: {
        beginAtZero: true,
        grid: { color: "#f1f5f9" },
        ticks: {
          font: { size: 11 },
          color: "#64748b",
          callback: (val) => {
            if (val >= 1000000) return `${(val / 1000000).toFixed(1)}M`;
            if (val >= 1000) return `${(val / 1000).toFixed(0)}k`;
            return val;
          },
        },
      },
    },
  };

  // Chart 2: Category Breakdown Doughnut
  const doughnutData = useMemo(() => {
    const fresh = summary.fresh_website_credits || 0;
    const edit = summary.edit_credits || 0;
    const other = summary.other_tool_credits || 0;

    return {
      labels: ["Fresh Websites (15k)", "Website Edits (1k)", "Other AI Tools"],
      datasets: [
        {
          data: [fresh, edit, other],
          backgroundColor: ["#623ce7", "#10b981", "#3b82f6"],
          borderColor: ["#ffffff", "#ffffff", "#ffffff"],
          borderWidth: 2,
          hoverOffset: 6,
        },
      ],
    };
  }, [summary]);

  const doughnutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: "bottom",
        labels: {
          boxWidth: 10,
          usePointStyle: true,
          font: { family: "sans-serif", size: 11 },
          color: "#475569",
        },
      },
      tooltip: {
        backgroundColor: "rgba(15, 23, 42, 0.92)",
        padding: 12,
        cornerRadius: 8,
        mode: "dataset",
        intersect: true,
        callbacks: {
          title: () => "Feature Share",
          label: (context) => {
            const val = context.parsed || 0;
            const total = summary.total_credits || 1;
            const pct = ((val / total) * 100).toFixed(1);
            return ` ${context.label}: ${val.toLocaleString()} credits (${pct}%)`;
          },
          footer: () => `Total Consumed: ${Number(summary.total_credits || 0).toLocaleString()} credits`,
        },
      },
    },
    cutout: "70%",
  };

  // Top Users List
  const topUsers = data?.top_users || [];

  // Filtered Users List
  const filteredUsers = useMemo(() => {
    return topUsers.filter((u) => {
      if (userTypeFilter === "registered" && u.is_guest) return false;
      if (userTypeFilter === "guest" && !u.is_guest) return false;

      if (userSearchQuery.trim() !== "") {
        const q = userSearchQuery.toLowerCase();
        const matchesName = u.name?.toLowerCase().includes(q);
        const matchesEmail = u.email?.toLowerCase().includes(q);
        const matchesId =
          u.user_id?.toLowerCase().includes(q) ||
          u.guest_user_id?.toLowerCase().includes(q);
        if (!matchesName && !matchesEmail && !matchesId) return false;
      }
      return true;
    });
  }, [topUsers, userTypeFilter, userSearchQuery]);

  const userTotalPages = Math.ceil(filteredUsers.length / userPageSize) || 1;
  const paginatedUsers = useMemo(() => {
    const start = (userPage - 1) * userPageSize;
    return filteredUsers.slice(start, start + userPageSize);
  }, [filteredUsers, userPage, userPageSize]);

  // Handle Users CSV Download
  const handleExportUsersCsv = () => {
    const users = filteredUsers || [];
    if (users.length === 0) {
      toast.info("No users available to export");
      return;
    }

    const headers = [
      "User Name",
      "User Email",
      "User ID",
      "Account Type",
      "Lifetime Credits",
      `${rangeLabel} Credits`,
      "Fresh Websites Count",
      "Fresh Websites Credits",
      "Edits Count",
      "Edits Credits",
      "Tools Credits",
      "Claimed Guest Sites",
      "Last Active Date",
    ];

    const rows = users.map((u) => [
      `"${(u.name || "").replace(/"/g, '""')}"`,
      `"${(u.email || "").replace(/"/g, '""')}"`,
      `"${u.user_id || ""}"`,
      u.is_guest ? "Guest Session" : "Registered User",
      u.lifetime_credits || 0,
      u.total_credits || 0,
      u.fresh_websites_count || 0,
      u.fresh_website_credits || 0,
      u.edits_count || 0,
      u.edit_credits || 0,
      u.other_tool_credits || 0,
      u.claimed_guest_count || 0,
      `"${u.last_active_date || ""}"`,
    ]);

    const csvContent = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `grigora-ai-users-${istDateKey()}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("AI users exported to CSV");
  };

  // Filtered Activity Logs
  const filteredLogs = useMemo(() => {
    const logs = data?.logs || [];
    return logs.filter((log) => {
      // Category filter
      if (categoryFilter === "Guest Builds") {
        if (!log.is_guest && !log.claimed_by) return false;
      } else if (categoryFilter !== "all" && log.category !== categoryFilter) {
        return false;
      }

      // Search query filter
      if (searchQuery.trim() !== "") {
        const q = searchQuery.toLowerCase();
        const matchesUser =
          log.user_name?.toLowerCase().includes(q) ||
          log.user_email?.toLowerCase().includes(q) ||
          log.user_id?.toLowerCase().includes(q) ||
          log.guest_user_id?.toLowerCase().includes(q);
        const matchesAction =
          log.action_label?.toLowerCase().includes(q) ||
          log.action_type?.toLowerCase().includes(q);
        const matchesPrompt = log.prompt?.toLowerCase().includes(q);
        if (!matchesUser && !matchesAction && !matchesPrompt) return false;
      }
      return true;
    });
  }, [data?.logs, categoryFilter, searchQuery]);

  // Pagination for logs table
  const totalPages = Math.ceil(filteredLogs.length / pageSize) || 1;
  const paginatedLogs = useMemo(() => {
    const start = (currentPage - 1) * pageSize;
    return filteredLogs.slice(start, start + pageSize);
  }, [filteredLogs, currentPage, pageSize]);

  // Handle CSV Download
  const handleExportCsv = () => {
    const logs = data?.logs || [];
    if (logs.length === 0) {
      toast.info("No logs available to export");
      return;
    }

    const headers = [
      "Timestamp",
      "User Name",
      "User Email",
      "User ID",
      "Is Guest",
      "Guest User ID",
      "Claimed By User ID",
      "Claimed At",
      "Category",
      "Action Type",
      "Action Label",
      "Credits Consumed",
      "Prompt / Context",
      "Project ID",
    ];

    const rows = logs.map((log) => [
      `"${istISOString(log.created_at)}"`,
      `"${(log.user_name || "").replace(/"/g, '""')}"`,
      `"${(log.user_email || "").replace(/"/g, '""')}"`,
      `"${log.user_id || ""}"`,
      log.is_guest ? "YES" : "NO",
      `"${log.guest_user_id || ""}"`,
      `"${log.claimed_by || ""}"`,
      `"${istISOString(log.claimed_at)}"`,
      `"${log.category || ""}"`,
      `"${log.action_type || ""}"`,
      `"${(log.action_label || "").replace(/"/g, '""')}"`,
      log.credits || 0,
      `"${(log.prompt || "").replace(/"/g, '""')}"`,
      `"${log.project_id || ""}"`,
    ]);

    const csvContent = [headers.join(","), ...rows.map((r) => r.join(","))].join("\n");
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.setAttribute("href", url);
    link.setAttribute(
      "download",
      `grigora-ai-usage-logs-${istDateKey()}.csv`
    );
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success("AI usage logs exported to CSV");
  };

  const handleCopyPrompt = (text, id) => {
    if (!text) return;
    navigator.clipboard.writeText(text);
    setCopiedPromptId(id);
    toast.success("Prompt copied to clipboard");
    setTimeout(() => setCopiedPromptId(null), 2000);
  };

  const getCategoryBadgeClass = (category) => {
    switch (category) {
      case "Fresh Website":
        return "bg-purple-50 text-[#623ce7] border-purple-200";
      case "Website Edit":
        return "bg-emerald-50 text-emerald-700 border-emerald-200";
      case "Other Tools":
      default:
        return "bg-blue-50 text-blue-700 border-blue-200";
    }
  };

  return (
    <>
      <HelmetLayout title="AI Usage Analytics" />
      <div className="space-y-8 pb-16 font-sans" aria-busy={loading || refreshing}>
        {loadError && <div role="alert" className="bg-red-50 border border-red-200 text-red-700 p-4 rounded-xl">Could not load AI usage. Please refresh to retry.</div>}
        <div className="flex flex-wrap items-center gap-2 text-xs font-medium text-slate-500">
          <span className="rounded-full bg-purple-50 text-[#623ce7] px-3 py-1">{rangeLabel}</span>
          <span>Calendar days in IST (Asia/Kolkata) · applies to all metrics, users and activity</span>
          {(loading || refreshing) && <span role="status">Updating…</span>}
        </div>
        {data?.reporting?.note && <p className="text-xs text-slate-500 rounded-lg border border-slate-200 bg-white p-3">{data.reporting.note}</p>}
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <div className="flex items-center gap-2.5 mb-1">
              <h1 className="text-2xl font-bold text-slate-900">AI Usage Analytics</h1>
              <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-semibold bg-[#623ce7]/10 text-[#623ce7] border border-[#623ce7]/20">
                <Sparkles size={12} />
                Live Tracking
              </span>
            </div>
            <p className="text-slate-500 text-sm">
              Track platform AI credit consumption across fresh websites, edits, guest builds, and AI tools.
            </p>
          </div>
          <div className="flex items-center gap-2.5">
            <button
              onClick={() => fetchData(true)}
              disabled={loading || refreshing}
              className="inline-flex items-center gap-2 bg-white hover:bg-slate-50 border border-slate-200 text-slate-700 px-3.5 py-2 rounded-lg text-sm font-medium transition-colors shadow-sm disabled:opacity-50"
            >
              <RefreshCw size={15} className={refreshing ? "animate-spin text-[#623ce7]" : ""} />
              <span>Refresh</span>
            </button>
            <button
              onClick={activeTab === "users" ? handleExportUsersCsv : handleExportCsv}
              disabled={
                loading ||
                (activeTab === "users"
                  ? filteredUsers.length === 0
                  : (data?.logs?.length || 0) === 0)
              }
              className="inline-flex items-center gap-2 bg-[#623ce7] hover:bg-[#5030bc] text-white px-4 py-2 rounded-lg text-sm font-medium transition-colors shadow-md shadow-[#623ce7]/25 disabled:opacity-50"
            >
              <Download size={15} />
              <span>Export {activeTab === "users" ? "Users" : "Logs"} CSV</span>
            </button>
          </div>
        </div>

        {/* Loading Spinner */}
        {loading && (
          <div className="flex flex-col items-center justify-center py-20 bg-white rounded-xl border border-slate-200 shadow-sm">
            <PulseLoader color="#623ce7" size={10} />
            <p className="mt-4 text-sm text-slate-500 font-medium">
              Aggregating AI usage data & guest logs...
            </p>
          </div>
        )}

        {!loading && (
          <>
            {/* KPI Cards Grid with K/M Suffix Format */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-5">
              <StatCard
                title="Total AI Credits"
                value={summary.total_credits}
                badge="100% Platform"
                icon={Zap}
                color={{
                  bg: "bg-[#623ce7]/10",
                  text: "text-[#623ce7]",
                  badge: "bg-purple-50 text-[#623ce7] border border-purple-200",
                }}
              />
              <StatCard
                title="Fresh Websites"
                value={summary.fresh_website_credits}
                badge={`${((summary.fresh_website_credits / (summary.total_credits || 1)) * 100).toFixed(1)}% Share`}
                icon={Globe}
                color={{
                  bg: "bg-violet-50",
                  text: "text-violet-600",
                  badge: "bg-violet-50 text-violet-700 border border-violet-200",
                }}
              />
              <StatCard
                title="Website Edits"
                value={summary.edit_credits}
                badge={`${((summary.edit_credits / (summary.total_credits || 1)) * 100).toFixed(1)}% Share`}
                icon={Sliders}
                color={{
                  bg: "bg-emerald-50",
                  text: "text-emerald-600",
                  badge: "bg-emerald-50 text-emerald-700 border border-emerald-200",
                }}
              />
              <StatCard
                title="Other AI Tools"
                value={summary.other_tool_credits}
                badge={`${((summary.other_tool_credits / (summary.total_credits || 1)) * 100).toFixed(1)}% Share`}
                icon={FileText}
                color={{
                  bg: "bg-blue-50",
                  text: "text-blue-600",
                  badge: "bg-blue-50 text-blue-700 border border-blue-200",
                }}
              />
              <StatCard
                title="Guest AI Credits"
                value={
                  summary.unclaimed_guest_credits ||
                  (summary.unclaimed_guest_count || 0) * 15000
                }
                badge={`${summary.unclaimed_guest_count || 0} Not Logged In`}
                icon={UserX}
                color={{
                  bg: "bg-amber-50",
                  text: "text-amber-600",
                  badge: "bg-amber-50 text-amber-700 border border-amber-200",
                }}
              />
            </div>

            {/* Charts Section */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Daily Consumption Chart (2 Cols) */}
              <div className="lg:col-span-2 bg-white rounded-xl border border-slate-200 p-6 shadow-sm flex flex-col justify-between">
                <div>
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
                    <div>
                      <div className="flex items-center gap-2">
                        <BarChart3 size={18} className="text-[#623ce7]" />
                        <h3 className="text-lg font-bold text-slate-900">
                          AI Credit Consumption Over Time
                        </h3>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Breakdown of daily credits consumed across websites, edits, and tools
                      </p>
                    </div>

                    {/* Chart Controls */}
                    <div className="flex items-center gap-2 flex-wrap">
                      {/* View toggle */}
                      <div className="inline-flex bg-slate-100 p-1 rounded-lg">
                        <button
                          type="button"
                          onClick={() => setChartType("bar")}
                          className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-all ${
                            chartType === "bar"
                              ? "bg-white text-slate-900 shadow-sm"
                              : "text-slate-500 hover:text-slate-800"
                          }`}
                        >
                          Stacked
                        </button>
                        <button
                          type="button"
                          onClick={() => setChartType("line")}
                          className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-all ${
                            chartType === "line"
                              ? "bg-white text-slate-900 shadow-sm"
                              : "text-slate-500 hover:text-slate-800"
                          }`}
                        >
                          Trends
                        </button>
                      </div>

                      {/* Timeframe pills */}
                      <div className="inline-flex bg-slate-100 p-1 rounded-lg">
                        {[
                          { label: "7D", val: 7 },
                          { label: "15D", val: 15 },
                          { label: "30D", val: 30 },
                          { label: "All", val: "all" },
                        ].map((btn) => (
                          <button
                            key={btn.label}
                            type="button"
                            onClick={() => setTimeframe(btn.val)}
                            className={`px-2.5 py-1 text-xs font-semibold rounded-md transition-all ${
                              timeframe === btn.val
                                ? "bg-[#623ce7] text-white shadow-sm"
                                : "text-slate-500 hover:text-slate-800"
                            }`}
                          >
                            {btn.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>

                  <div className="h-72 w-full">
                    {filteredTimeline.length > 0 ? (
                      chartType === "bar" ? (
                        <Bar data={consumptionChartData} options={consumptionChartOptions} />
                      ) : (
                        <Line data={consumptionChartData} options={consumptionChartOptions} />
                      )
                    ) : (
                      <div className="h-full flex items-center justify-center text-slate-400 text-sm">
                        No consumption data for this timeframe
                      </div>
                    )}
                  </div>
                </div>

                <div className="mt-4 pt-3 border-t border-slate-100 flex items-center justify-between text-xs text-slate-500">
                  <div className="flex items-center gap-4 flex-wrap">
                    <div className="flex items-center gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-full bg-[#623ce7]" />
                      <span>Fresh Website (15k)</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-full bg-[#10b981]" />
                      <span>Edits (1k)</span>
                    </div>
                    <div className="flex items-center gap-1.5">
                      <span className="w-2.5 h-2.5 rounded-full bg-[#3b82f6]" />
                      <span>Other AI Tools</span>
                    </div>
                  </div>
                  <span className="text-[11px] text-slate-400">
                    Showing {filteredTimeline.length} calendar days
                  </span>
                </div>
              </div>

              {/* Usage Breakdown Doughnut (1 Col) */}
              <div className="bg-white rounded-xl border border-slate-200 p-6 shadow-sm flex flex-col justify-between">
                <div>
                  <div className="flex items-center gap-2 mb-1">
                    <PieChart size={18} className="text-[#623ce7]" />
                    <h3 className="text-lg font-bold text-slate-900">Feature Share</h3>
                  </div>
                  <p className="text-xs text-slate-500 mb-6">
                    Estimated feature share for the selected period
                  </p>

                  <div className="h-56 relative flex items-center justify-center">
                    <Doughnut data={doughnutData} options={doughnutOptions} />
                    <div className="absolute inset-0 flex flex-col items-center justify-center pointer-events-none pb-6">
                      <span className="text-xs text-slate-400 font-medium">Total Consumed</span>
                      <span className="text-lg font-extrabold text-slate-900">
                        {formatMetricNumber(summary.total_credits)}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Percentage Legend */}
                <div className="mt-4 pt-3 border-t border-slate-100 space-y-2 text-xs">
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-1.5 text-slate-600">
                      <span className="w-2 h-2 rounded-full bg-[#623ce7]" />
                      Fresh Websites
                    </span>
                    <span className="font-semibold text-slate-900">
                      {formatMetricNumber(summary.fresh_website_credits)} (
                      {(
                        (summary.fresh_website_credits / (summary.total_credits || 1)) *
                        100
                      ).toFixed(1)}
                      %)
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-1.5 text-slate-600">
                      <span className="w-2 h-2 rounded-full bg-[#10b981]" />
                      Website Edits
                    </span>
                    <span className="font-semibold text-slate-900">
                      {formatMetricNumber(summary.edit_credits)} (
                      {((summary.edit_credits / (summary.total_credits || 1)) * 100).toFixed(1)}
                      %)
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="flex items-center gap-1.5 text-slate-600">
                      <span className="w-2 h-2 rounded-full bg-[#3b82f6]" />
                      Other AI Tools
                    </span>
                    <span className="font-semibold text-slate-900">
                      {summary.other_tool_credits.toLocaleString()} (
                      {(
                        (summary.other_tool_credits / (summary.total_credits || 1)) *
                        100
                      ).toFixed(1)}
                      %)
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Tabs Header */}
            <div className="flex items-center gap-2 border-b border-slate-200">
              <button
                type="button"
                onClick={() => setActiveTab("users")}
                className={`flex items-center gap-2 pb-3.5 pt-1 text-sm font-semibold border-b-2 transition-colors ${
                  activeTab === "users"
                    ? "border-[#623ce7] text-[#623ce7]"
                    : "border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300"
                }`}
              >
                <Users size={16} />
                <span>Top Users</span>
                <span
                  className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    activeTab === "users"
                      ? "bg-[#623ce7]/10 text-[#623ce7]"
                      : "bg-slate-100 text-slate-600"
                  }`}
                >
                  {(topUsers?.length || 0).toLocaleString()}
                </span>
              </button>
              <button
                type="button"
                onClick={() => setActiveTab("logs")}
                className={`flex items-center gap-2 pb-3.5 pt-1 text-sm font-semibold border-b-2 transition-colors ${
                  activeTab === "logs"
                    ? "border-[#623ce7] text-[#623ce7]"
                    : "border-transparent text-slate-500 hover:text-slate-800 hover:border-slate-300"
                }`}
              >
                <Activity size={16} />
                <span>Activity Log</span>
                <span
                  className={`text-xs px-2 py-0.5 rounded-full font-medium ${
                    activeTab === "logs"
                      ? "bg-[#623ce7]/10 text-[#623ce7]"
                      : "bg-slate-100 text-slate-600"
                  }`}
                >
                  {(data?.logs?.length || 0).toLocaleString()}
                </span>
              </button>
            </div>

            {/* Tab 1: Top Users & Guest Sessions Table */}
            {activeTab === "users" && (
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
                <div className="p-6 border-b border-slate-100 flex flex-col gap-4">
                  <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                    <div>
                      <div className="flex items-center gap-2">
                        <Users size={18} className="text-[#623ce7]" />
                        <h3 className="text-lg font-bold text-slate-900">
                          Top Users & Guest Sessions by AI Consumption
                        </h3>
                      </div>
                      <p className="text-xs text-slate-500 mt-0.5">
                        Ranked by credits in the selected period. Lifetime credits stay visible. Unclaimed guest sessions are mapped to their unique guest ID until claimed.
                      </p>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-semibold text-slate-600 bg-slate-100 px-3 py-1 rounded-full">
                        {topUsers.filter((u) => !u.is_guest).length} Registered
                      </span>
                      <span className="text-xs font-semibold text-amber-700 bg-amber-50 border border-amber-200 px-3 py-1 rounded-full">
                        {topUsers.filter((u) => u.is_guest).length} Guest Session
                        {topUsers.filter((u) => u.is_guest).length !== 1 ? "s" : ""}
                      </span>
                    </div>
                  </div>

                  {/* Filter Toolbar */}
                  <div className="flex flex-col md:flex-row items-center justify-between gap-3 pt-2">
                    {/* Search Input */}
                    <div className="relative w-full md:w-80">
                      <Search
                        size={16}
                        className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                      />
                      <input
                        type="text"
                        value={userSearchQuery}
                        onChange={(e) => {
                          setUserSearchQuery(e.target.value);
                          setUserPage(1);
                        }}
                        placeholder="Search user name, email, ID..."
                        className="w-full pl-9 pr-4 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#623ce7]/20 focus:border-[#623ce7] transition-all"
                      />
                      {userSearchQuery && (
                        <button
                          onClick={() => {
                            setUserSearchQuery("");
                            setUserPage(1);
                          }}
                          className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>

                    {/* Account Type Filter Pills */}
                    <div className="flex items-center gap-1.5 w-full md:w-auto overflow-x-auto pb-1 md:pb-0">
                      {[
                        { label: "All Accounts", val: "all", count: topUsers.length },
                        {
                          label: "Registered Users",
                          val: "registered",
                          count: topUsers.filter((u) => !u.is_guest).length,
                        },
                        {
                          label: "Guest Sessions",
                          val: "guest",
                          count: topUsers.filter((u) => u.is_guest).length,
                        },
                      ].map((btn) => (
                        <button
                          key={btn.val}
                          type="button"
                          onClick={() => {
                            setUserTypeFilter(btn.val);
                            setUserPage(1);
                          }}
                          className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all flex items-center gap-1.5 ${
                            userTypeFilter === btn.val
                              ? "bg-[#623ce7] text-white shadow-sm"
                              : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                          }`}
                        >
                          <span>{btn.label}</span>
                          <span
                            className={`text-[10px] px-1.5 py-0.2 rounded-full font-bold ${
                              userTypeFilter === btn.val
                                ? "bg-white/20 text-white"
                                : "bg-slate-200 text-slate-700"
                            }`}
                          >
                            {btn.count}
                          </span>
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="overflow-x-auto">
                  <table className="w-full text-left border-collapse text-sm">
                    <thead>
                      <tr className="bg-slate-50/75 border-b border-slate-200 text-slate-500 font-semibold text-xs">
                        <th className="py-3 px-6">User / Account</th>
                        <th className="py-3 px-6">Type & Status</th>
                        <th className="py-3 px-6 text-right">Lifetime Credits</th>
                        <th className="py-3 px-6 text-right">{rangeLabel} Credits</th>
                        <th className="py-3 px-6 text-center">Fresh Sites</th>
                        <th className="py-3 px-6 text-center">Prompt Edits</th>
                        <th className="py-3 px-6 text-right">Tools Credits</th>
                        <th className="py-3 px-6 text-center">Last Active</th>
                        <th className="py-3 px-6 text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {paginatedUsers.map((user, idx) => {
                        const isGuest = user.is_guest;
                        const initial = isGuest
                          ? "G"
                          : (user.name || user.email || "U").charAt(0).toUpperCase();
                        const rank = (userPage - 1) * userPageSize + idx + 1;

                        return (
                          <tr
                            key={user.user_id || idx}
                            className="hover:bg-slate-50/60 transition-colors"
                          >
                            {/* User info */}
                            <td className="py-4 px-6">
                              <div className="flex items-center gap-3">
                                <div
                                  className={`w-9 h-9 rounded-full font-bold text-xs flex items-center justify-center shrink-0 border ${
                                    isGuest
                                      ? "bg-amber-50 text-amber-600 border-amber-200"
                                      : "bg-[#623ce7]/10 text-[#623ce7] border-[#623ce7]/20"
                                  }`}
                                >
                                  {initial}
                                </div>
                                <div className="min-w-0">
                                  <div className="flex items-center gap-1.5">
                                    <span className="font-semibold text-slate-900 truncate">
                                      {user.name}
                                    </span>
                                    <span className="text-[10px] text-slate-400 font-mono">
                                      #{rank}
                                    </span>
                                  </div>
                                  <p className="text-xs text-slate-500 truncate font-mono">
                                    {user.email}
                                  </p>
                                </div>
                              </div>
                            </td>

                            {/* Type & Status */}
                            <td className="py-4 px-6 whitespace-nowrap">
                              {isGuest ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-amber-50 text-amber-700 border border-amber-200">
                                  <UserX size={11} />
                                  Unclaimed Guest
                                </span>
                              ) : user.claimed_guest_count > 0 ? (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold bg-purple-50 text-[#623ce7] border border-purple-200">
                                  <UserCheck size={11} />
                                  Claimed Guest Site ({user.claimed_guest_count})
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-medium bg-slate-100 text-slate-600">
                                  Registered User
                                </span>
                              )}
                            </td>

                            <td className="py-4 px-6 text-right text-sm font-semibold text-slate-600" title={`${(user.lifetime_credits || 0).toLocaleString()} credits`}>
                              {formatMetricNumber(user.lifetime_credits)}
                            </td>
                            {/* Selected-period credits */}
                            <td className="py-4 px-6 text-right">
                              <span
                                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-bold bg-purple-50 text-[#623ce7] border border-purple-200"
                                title={`${user.total_credits.toLocaleString()} credits`}
                              >
                                <Zap size={11} />
                                {formatMetricNumber(user.total_credits)}
                              </span>
                            </td>

                            {/* Fresh Sites */}
                            <td className="py-4 px-6 text-center">
                              <div className="flex flex-col items-center">
                                <span className="font-semibold text-slate-800">
                                  {user.fresh_websites_count} site
                                  {user.fresh_websites_count !== 1 ? "s" : ""}
                                </span>
                                <span className="text-[11px] text-slate-400 font-mono">
                                  {formatMetricNumber(user.fresh_website_credits)} cr
                                </span>
                              </div>
                            </td>

                            {/* Edits */}
                            <td className="py-4 px-6 text-center">
                              <div className="flex flex-col items-center">
                                <span className="font-semibold text-slate-800">
                                  {user.edits_count} edit{user.edits_count !== 1 ? "s" : ""}
                                </span>
                                <span className="text-[11px] text-slate-400 font-mono">
                                  {formatMetricNumber(user.edit_credits)} cr
                                </span>
                              </div>
                            </td>

                            {/* Tools Credits */}
                            <td className="py-4 px-6 text-right">
                              <span className="font-mono text-xs text-slate-700 font-medium">
                                {user.other_tool_credits.toLocaleString()} cr
                              </span>
                            </td>

                            {/* Last Active */}
                            <td className="py-4 px-6 text-center text-xs text-slate-500">
                              {user.last_active_date || "—"}
                            </td>

                            {/* Actions */}
                            <td className="py-4 px-6 text-right">
                              {isGuest ? (
                                <span className="text-[11px] text-slate-400 italic">
                                  Guest Session
                                </span>
                              ) : (
                                <Link target="_blank" rel="noopener noreferrer"
                                  to={`/users/${user.user_id}`}
                                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium text-[#623ce7] bg-[#623ce7]/5 hover:bg-[#623ce7]/10 transition-colors"
                                >
                                  <span>View Profile</span>
                                  <ArrowUpRight size={13} />
                                </Link>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                      {paginatedUsers.length === 0 && (
                        <tr>
                          <td colSpan={9} className="py-8 text-center text-slate-400 text-sm">
                            No users match your filters.
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                {/* Users Pagination Footer */}
                <div className="p-4 border-t border-slate-100 flex flex-col sm:flex-row justify-between items-center gap-3 text-xs text-slate-500">
                  <div className="flex items-center gap-2">
                    <span>Rows per page:</span>
                    <select
                      value={userPageSize}
                      onChange={(e) => {
                        setUserPageSize(Number(e.target.value));
                        setUserPage(1);
                      }}
                      className="border border-slate-200 rounded px-2 py-1 text-xs focus:outline-none focus:border-[#623ce7]"
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                    <span className="text-slate-400">
                      Showing{" "}
                      {filteredUsers.length === 0
                        ? 0
                        : (userPage - 1) * userPageSize + 1}
                      -
                      {Math.min(userPage * userPageSize, filteredUsers.length)} of{" "}
                      {filteredUsers.length.toLocaleString()}
                    </span>
                  </div>

                  <div className="flex items-center gap-2">
                    <span>
                      Page {userPage} of {userTotalPages}
                    </span>
                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => setUserPage((p) => Math.max(1, p - 1))}
                        disabled={userPage <= 1}
                        className="p-1.5 rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-transparent"
                      >
                        <ChevronLeft size={14} />
                      </button>
                      <button
                        onClick={() => setUserPage((p) => Math.min(userTotalPages, p + 1))}
                        disabled={userPage >= userTotalPages}
                        className="p-1.5 rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-transparent"
                      >
                        <ChevronRight size={14} />
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Tab 2: AI Activity & Audit Logs Table */}
            {activeTab === "logs" && (
              <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden">
              <div className="p-6 border-b border-slate-100 flex flex-col gap-4">
                <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
                  <div>
                    <div className="flex items-center gap-2">
                      <Sparkles size={18} className="text-[#623ce7]" />
                      <h3 className="text-lg font-bold text-slate-900">
                        AI Activity & Prompt Audit Log
                      </h3>
                    </div>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Chronological log of every website generation, guest creation, prompt edit, and AI tool invocation.
                    </p>
                  </div>
                  <span className="text-xs text-slate-400 font-medium">
                    Showing {filteredLogs.length} of {data?.logs?.length || 0} operations
                  </span>
                </div>

                {/* Filter Toolbar */}
                <div className="flex flex-col md:flex-row items-center justify-between gap-3 pt-2">
                  {/* Search Input */}
                  <div className="relative w-full md:w-80">
                    <Search
                      size={16}
                      className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400"
                    />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={(e) => {
                        setSearchQuery(e.target.value);
                        setCurrentPage(1);
                      }}
                      placeholder="Search user, action, guest ID, prompt..."
                      className="w-full pl-9 pr-4 py-2 rounded-lg border border-slate-200 text-sm focus:outline-none focus:ring-2 focus:ring-[#623ce7]/20 focus:border-[#623ce7] transition-all"
                    />
                    {searchQuery && (
                      <button
                        onClick={() => setSearchQuery("")}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        <X size={14} />
                      </button>
                    )}
                  </div>

                  {/* Category Filter Pills */}
                  <div className="flex items-center gap-1.5 w-full md:w-auto overflow-x-auto pb-1 md:pb-0">
                    {[
                      { label: "All Operations", val: "all" },
                      { label: "Fresh Websites", val: "Fresh Website" },
                      { label: "Website Edits", val: "Website Edit" },
                      { label: "Guest Builds", val: "Guest Builds" },
                      { label: "Other Tools", val: "Other Tools" },
                    ].map((btn) => (
                      <button
                        key={btn.val}
                        type="button"
                        onClick={() => {
                          setCategoryFilter(btn.val);
                          setCurrentPage(1);
                        }}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-all ${
                          categoryFilter === btn.val
                            ? "bg-[#623ce7] text-white shadow-sm"
                            : "bg-slate-100 text-slate-600 hover:bg-slate-200"
                        }`}
                      >
                        {btn.label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Table */}
              <div className="overflow-x-auto">
                <table className="w-full text-left border-collapse text-sm">
                  <thead>
                    <tr className="bg-slate-50/75 border-b border-slate-200 text-slate-500 font-semibold text-xs">
                      <th className="py-3 px-6">Timestamp</th>
                      <th className="py-3 px-6">User / Origin</th>
                      <th className="py-3 px-6">Category</th>
                      <th className="py-3 px-6">Action / Feature</th>
                      <th className="py-3 px-6 text-right">Credits</th>
                      <th className="py-3 px-6">Prompt / Details</th>
                      <th className="py-3 px-6 text-center">Inspect</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {paginatedLogs.map((log) => {
                      const dateObj = new Date(log.created_at);
                      const formattedDate = !isNaN(dateObj)
                        ? dateObj.toLocaleDateString("en-IN", { timeZone: "Asia/Kolkata",
                            month: "short",
                            day: "numeric",
                            year: "numeric",
                          })
                        : "—";
                      const formattedTime = log.time_precision === "day" ? "Daily total · time unavailable" : !isNaN(dateObj)
                        ? dateObj.toLocaleTimeString("en-IN", { timeZone: "Asia/Kolkata", timeZoneName: "short",
                            hour: "2-digit",
                            minute: "2-digit",
                          })
                        : "";

                      return (
                        <tr key={log.id} className="hover:bg-slate-50/60 transition-colors">
                          {/* Timestamp */}
                          <td className="py-3.5 px-6 whitespace-nowrap text-xs">
                            <div className="font-medium text-slate-900">{formattedDate}</div>
                            <div className="text-slate-400 text-[11px]">{formattedTime}</div>
                          </td>

                          {/* User / Origin */}
                          <td className="py-3.5 px-6 whitespace-nowrap">
                            <div className="flex items-center gap-1.5">
                              <span className="font-semibold text-slate-800 text-xs truncate max-w-[140px]">
                                {log.user_name}
                              </span>
                              {log.is_guest ? (
                                <span className="text-[10px] px-1.5 py-0.2 rounded bg-amber-50 text-amber-700 border border-amber-200 font-medium">
                                  Guest
                                </span>
                              ) : log.claimed_by ? (
                                <span className="text-[10px] px-1.5 py-0.2 rounded bg-purple-50 text-[#623ce7] border border-purple-200 font-medium">
                                  Claimed
                                </span>
                              ) : null}
                            </div>
                            <div className="text-slate-400 text-[11px] truncate max-w-[170px] font-mono">
                              {log.user_email}
                            </div>
                          </td>

                          {/* Category */}
                          <td className="py-3.5 px-6 whitespace-nowrap">
                            <span
                              className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-semibold border ${getCategoryBadgeClass(
                                log.category
                              )}`}
                            >
                              {log.category}
                            </span>
                          </td>

                          {/* Action */}
                          <td className="py-3.5 px-6 text-xs max-w-[240px]">
                            <p className="font-medium text-slate-800 truncate">
                              {log.action_label}
                            </p>
                            <p className="text-[11px] text-slate-400 font-mono truncate">
                              {log.action_type}
                            </p>
                          </td>

                          {/* Credits Consumed */}
                          <td className="py-3.5 px-6 text-right whitespace-nowrap">
                            <span className="font-mono font-bold text-xs text-slate-900 bg-slate-100 px-2 py-1 rounded">
                              {log.credits >= 1000
                                ? formatMetricNumber(log.credits)
                                : log.credits.toLocaleString()}{" "}
                              cr
                            </span>
                          </td>

                          {/* Prompt snippet */}
                          <td className="py-3.5 px-6 text-xs max-w-[280px]">
                            <p className="text-slate-600 line-clamp-2 italic">
                              "{log.prompt || "No prompt details"}"
                            </p>
                          </td>

                          {/* Inspect Modal Trigger */}
                          <td className="py-3.5 px-6 text-center whitespace-nowrap">
                            <button
                              onClick={() => setInspectedPrompt(log)}
                              className="p-1.5 rounded-md text-slate-400 hover:text-[#623ce7] hover:bg-[#623ce7]/10 transition-colors"
                              title="Inspect Full Prompt & Metadata"
                            >
                              <Eye size={16} />
                            </button>
                          </td>
                        </tr>
                      );
                    })}

                    {paginatedLogs.length === 0 && (
                      <tr>
                        <td colSpan={7} className="py-12 text-center text-slate-400 text-sm">
                          No activity logs match your filters.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>

              {/* Pagination Footer */}
              <div className="p-4 border-t border-slate-100 flex flex-col sm:flex-row justify-between items-center gap-3 text-xs text-slate-500">
                <div className="flex items-center gap-2">
                  <span>Rows per page:</span>
                  <select
                    value={pageSize}
                    onChange={(e) => {
                      setPageSize(Number(e.target.value));
                      setCurrentPage(1);
                    }}
                    className="border border-slate-200 rounded px-2 py-1 text-xs focus:outline-none focus:border-[#623ce7]"
                  >
                    <option value={10}>10</option>
                    <option value={25}>25</option>
                    <option value={50}>50</option>
                  </select>
                </div>

                <div className="flex items-center gap-2">
                  <span>
                    Page {currentPage} of {totalPages}
                  </span>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                      disabled={currentPage <= 1}
                      className="p-1.5 rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-transparent"
                    >
                      <ChevronLeft size={14} />
                    </button>
                    <button
                      onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                      disabled={currentPage >= totalPages}
                      className="p-1.5 rounded border border-slate-200 hover:bg-slate-50 disabled:opacity-40 disabled:hover:bg-transparent"
                    >
                      <ChevronRight size={14} />
                    </button>
                  </div>
                </div>
              </div>
            </div>
            )}
          </>
        )}

        {/* Prompt & Metadata Inspection Modal */}
        {inspectedPrompt && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/50 backdrop-blur-sm">
            <div className="bg-white rounded-2xl max-w-2xl w-full p-6 shadow-2xl border border-slate-100 flex flex-col max-h-[85vh] animate-in fade-in zoom-in-95 duration-200">
              <div className="flex items-center justify-between pb-4 border-b border-slate-100">
                <div className="flex items-center gap-2.5">
                  <div className="p-2 rounded-lg bg-[#623ce7]/10 text-[#623ce7]">
                    <Sparkles size={18} />
                  </div>
                  <div>
                    <h3 className="text-base font-bold text-slate-900">
                      AI Operation & Prompt Details
                    </h3>
                    <p className="text-xs text-slate-400">
                      {inspectedPrompt.action_label} • {inspectedPrompt.credits.toLocaleString()}{" "}
                      credits
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setInspectedPrompt(null)}
                  className="p-1.5 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-100 transition-colors"
                >
                  <X size={18} />
                </button>
              </div>

              {/* Modal Body */}
              <div className="py-4 space-y-4 overflow-y-auto flex-1">
                {/* User & Meta Badges */}
                <div className="grid grid-cols-2 gap-3 text-xs bg-slate-50 p-3.5 rounded-xl border border-slate-100">
                  <div>
                    <span className="text-slate-400 block font-medium">User / Account</span>
                    <span className="font-semibold text-slate-900 flex items-center gap-1.5">
                      {inspectedPrompt.user_name}
                      {inspectedPrompt.is_guest && (
                        <span className="text-[10px] px-1.5 py-0.2 rounded bg-amber-100 text-amber-800">
                          Guest
                        </span>
                      )}
                    </span>
                    <span className="text-slate-500 block font-mono">
                      {inspectedPrompt.user_email}
                    </span>
                  </div>
                  <div>
                    <span className="text-slate-400 block font-medium">Timestamp</span>
                    <span className="font-semibold text-slate-900">
                      {inspectedPrompt.time_precision === "day" ? `${formatISTDateTime(inspectedPrompt.created_at, { hour: undefined, minute: undefined })} · time unavailable` : formatISTDateTime(inspectedPrompt.created_at)}
                    </span>
                    <span className="text-slate-500 block font-mono">
                      Category: {inspectedPrompt.category}
                    </span>
                  </div>
                </div>

                {/* Guest Origin & Claim Status Trail */}
                {(inspectedPrompt.guest_user_id || inspectedPrompt.is_guest) && (
                  <div className="p-3 bg-amber-50/70 border border-amber-200 rounded-xl text-xs space-y-1">
                    <span className="font-semibold text-amber-900 block">
                      Guest Build Audit Trail:
                    </span>
                    <p className="text-amber-800 font-mono text-[11px]">
                      Guest User ID: {inspectedPrompt.guest_user_id || inspectedPrompt.user_id}
                    </p>
                    {inspectedPrompt.claimed_by ? (
                      <p className="text-emerald-700 font-medium text-[11px]">
                        ✓ Claimed by registered user: {inspectedPrompt.claimed_user_name} (
                        {inspectedPrompt.claimed_user_email}) on{" "}
                        {formatISTDateTime(inspectedPrompt.claimed_at)}
                      </p>
                    ) : (
                      <p className="text-amber-700 italic text-[11px]">
                        ⏳ Unclaimed guest creation. Session has not been claimed by a logged-in account.
                      </p>
                    )}
                  </div>
                )}

                {/* Prompt Box */}
                <div>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-xs font-bold text-slate-700 uppercase tracking-wider">
                      User Prompt / Instruction
                    </span>
                    <button
                      onClick={() =>
                        handleCopyPrompt(inspectedPrompt.prompt, inspectedPrompt.id)
                      }
                      className="inline-flex items-center gap-1 text-xs text-[#623ce7] hover:text-[#5030bc] font-medium transition-colors"
                    >
                      {copiedPromptId === inspectedPrompt.id ? (
                        <>
                          <Check size={13} className="text-emerald-600" />
                          <span className="text-emerald-600">Copied</span>
                        </>
                      ) : (
                        <>
                          <Copy size={13} />
                          <span>Copy Prompt</span>
                        </>
                      )}
                    </button>
                  </div>
                  <div className="bg-slate-900 text-slate-100 p-4 rounded-xl text-xs font-mono whitespace-pre-wrap max-h-60 overflow-y-auto border border-slate-800 leading-relaxed select-text">
                    {inspectedPrompt.prompt || "No prompt text recorded."}
                  </div>
                </div>

                {inspectedPrompt.project_id && (
                  <div className="text-xs text-slate-500">
                    Associated Project ID:{" "}
                    <span className="font-mono text-slate-800 font-semibold">
                      {inspectedPrompt.project_id}
                    </span>
                  </div>
                )}
              </div>

              {/* Modal Footer */}
              <div className="pt-3 border-t border-slate-100 flex items-center justify-between">
                {!inspectedPrompt.is_guest && inspectedPrompt.user_id ? (
                  <Link target="_blank" rel="noopener noreferrer"
                    to={`/users/${inspectedPrompt.user_id}`}
                    className="inline-flex items-center gap-1.5 text-xs font-medium text-[#623ce7] hover:underline"
                  >
                    <User size={13} />
                    <span>View User in Admin</span>
                  </Link>
                ) : (
                  <span className="text-xs text-slate-400 italic">Guest Session (No Cognito Profile)</span>
                )}
                <button
                  onClick={() => setInspectedPrompt(null)}
                  className="px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 text-xs font-semibold rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
}
